import { useCallback, useState } from 'react';
import { Track } from '../components/PageComponents/trackList/track';

export interface TrackMenuState {
    track: Track;
    x: number;
    y: number;
    // Index of this row within the current playlist's track list, set only
    // when the menu was opened from a playlist detail page - lets "Remove
    // from this playlist" remove the exact row rather than the first track
    // with a matching id, since a playlist can contain the same track twice.
    playlistItemIndex?: number;
}

const MENU_WIDTH = 240;
const MENU_MARGIN = 8;

function clamp(x: number, y: number) {
    const maxX = window.innerWidth - MENU_WIDTH - MENU_MARGIN;
    const maxY = window.innerHeight - MENU_MARGIN;
    return { x: Math.max(MENU_MARGIN, Math.min(x, maxX)), y: Math.max(MENU_MARGIN, Math.min(y, maxY)) };
}

// Shared open/close state for the track right-click / kebab-button menu,
// used identically across the library grid, album/artist detail, and
// playlist detail track lists.
export function useTrackMenu() {
    const [menu, setMenu] = useState<TrackMenuState | null>(null);
    // Lifted out of TrackMenu itself so the info panel survives the
    // dropdown closing (TrackMenu unmounts on close, which would otherwise
    // destroy any state living inside it).
    const [infoTrack, setInfoTrack] = useState<Track | null>(null);

    const openAtCursor = useCallback((e: React.MouseEvent, track: Track, playlistItemIndex?: number) => {
        e.preventDefault();
        e.stopPropagation();
        const { x, y } = clamp(e.clientX, e.clientY);
        setMenu({ track, x, y, playlistItemIndex });
    }, []);

    const openAtElement = useCallback(
        (e: React.MouseEvent<HTMLElement>, track: Track, playlistItemIndex?: number) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            const { x, y } = clamp(rect.right - MENU_WIDTH, rect.bottom + 4);
            setMenu({ track, x, y, playlistItemIndex });
        },
        []
    );

    const closeMenu = useCallback(() => setMenu(null), []);

    const openTrackInfo = useCallback((track: Track) => setInfoTrack(track), []);
    const closeTrackInfo = useCallback(() => setInfoTrack(null), []);

    return { menu, openAtCursor, openAtElement, closeMenu, infoTrack, openTrackInfo, closeTrackInfo };
}
